/**
 * VotingAnnouncementModal
 *
 * Full-screen reveal for Employee of the Month results.
 *
 * Shown once per user per period when a voting period closes with an
 * announcement. Displays:
 *   - The winner (hero card with name, role, % of votes)
 *   - Anonymous reasons — the "why" from every voter who wrote one.
 *     Voter identity is NEVER displayed here.
 *   - Runners-up — other people who received votes, with vote counts but
 *     no reasons and no voter names.
 *
 * Mobile-first: full-screen on phones, max-w-2xl centered on desktop.
 */

import { getLocaleSync } from '@/utils/locale';
import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, X, Sparkles, Users, Heart, Award } from 'lucide-react';
import type { VotingResults } from '@/types';
import { getUserPhotoUrl } from '@/services/user-profile.service';

interface VotingAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: VotingResults | null;
}

export default function VotingAnnouncementModal({
  isOpen,
  onClose,
  results,
}: VotingAnnouncementModalProps) {
  const winnerPercentage = useMemo(() => {
    if (!results || !results.winnerId) return 0;
    const top = results.results?.find((r) => r.userId === results.winnerId);
    return top?.percentage ?? 0;
  }, [results]);

  const winnerVotes = useMemo(() => {
    if (!results || !results.winnerId) return 0;
    const top = results.results?.find((r) => r.userId === results.winnerId);
    return top?.voteCount ?? 0;
  }, [results]);

  const periodLabel = useMemo(() => {
    if (!results?.month) return '';
    const [y, m] = results.month.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString(getLocaleSync(), { month: 'long', year: 'numeric' });
  }, [results]);

  // Fetch winner photo (if any) — fire-and-forget, badge shows initials fallback
  const [winnerPhoto, setWinnerPhoto] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen || !results?.winnerId) {
      setWinnerPhoto(null);
      return;
    }
    let cancelled = false;
    getUserPhotoUrl(results.winnerId).then((url) => {
      if (!cancelled) setWinnerPhoto(url);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, results?.winnerId]);

  const winnerInitials = useMemo(() => {
    if (!results?.winnerName) return '★';
    return results.winnerName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('');
  }, [results?.winnerName]);

  if (!isOpen || !results || !results.winnerId) return null;

  const reasons = results.winnerReasons || [];
  const runnersUp = results.runnersUp || [];

  return (
    <AnimatePresence>
      <motion.div
        key="announcement-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center sm:p-4"
        onClick={onClose}
      >
        <motion.div
          key="announcement-card"
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          className="
            bg-white dark:bg-gray-900 shadow-2xl overflow-hidden flex flex-col
            w-full sm:max-w-2xl
            h-[92vh] sm:h-auto sm:max-h-[90vh]
            rounded-t-3xl sm:rounded-2xl
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero */}
          <div className="relative bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-white overflow-hidden shrink-0">
            {/* Decorative sparkle background */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <Sparkles className="absolute top-4 left-6 w-6 h-6" />
              <Sparkles className="absolute top-12 right-10 w-4 h-4" />
              <Sparkles className="absolute bottom-6 left-12 w-5 h-5" />
              <Trophy className="absolute -right-6 -bottom-6 w-40 h-40 opacity-30" />
            </div>

            {/* Mobile close chevron */}
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative px-6 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold opacity-90 mb-2">
                <Award className="w-4 h-4" />
                Employee of the Month
              </div>
              <div className="text-sm opacity-90 mb-4">{periodLabel}</div>

              {/* Winner photo + name — big and bold */}
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, type: 'spring', damping: 16 }}
                className="flex items-center gap-4"
              >
                <div className="relative shrink-0">
                  {winnerPhoto ? (
                    <img
                      src={winnerPhoto}
                      alt={results.winnerName}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-white/80 shadow-xl"
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/25 border-4 border-white/80 shadow-xl flex items-center justify-center text-xl sm:text-2xl font-bold text-white">
                      {winnerInitials}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-300 border-2 border-white flex items-center justify-center shadow-lg">
                    <Trophy className="w-4 h-4 text-amber-900" />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-2xl sm:text-3xl font-bold leading-tight truncate">
                    {results.winnerName}
                  </div>
                  {results.winnerRole && (
                    <div className="text-sm opacity-90 capitalize">
                      {results.winnerRole}
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Vote summary */}
              <div className="mt-4 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <Heart className="w-4 h-4" />
                  <span>
                    {winnerVotes} {winnerVotes === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
                {winnerPercentage > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">{winnerPercentage}%</span>
                    <span className="opacity-80">of the team</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  <span>
                    {results.totalVotes}{' '}
                    {results.totalVotes === 1 ? 'voter' : 'voters'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6 space-y-6">
            {/* Anonymous reasons */}
            {reasons.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-amber-500 rounded-full" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    What the team said
                  </h3>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 italic">
                  Comments shown anonymously — voter names are never shared.
                </p>
                <div className="space-y-3">
                  {reasons.map((reason, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.04 }}
                      className="
                        bg-amber-50 dark:bg-amber-900/15
                        border-l-4 border-amber-400 dark:border-amber-500
                        rounded-r-lg px-4 py-3
                      "
                    >
                      <p className="text-sm sm:text-[15px] text-gray-800 dark:text-gray-200 leading-relaxed">
                        &ldquo;{reason}&rdquo;
                      </p>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {reasons.length === 0 && (
              <section className="text-center py-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No written comments this month — just the votes.
                </p>
              </section>
            )}

            {/* Runners-up */}
            {runnersUp.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-gray-400 rounded-full" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    Also got votes
                  </h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden">
                  {runnersUp.map((r, i) => (
                    <div
                      key={r.userId}
                      className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                          {i + 2}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {r.userName}
                          </div>
                          {r.userRole && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {r.userRole}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 shrink-0 ml-3">
                        {r.voteCount}{' '}
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                          {r.voteCount === 1 ? 'vote' : 'votes'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Participation footer */}
            <section className="text-center pt-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {results.participationRate}% of the team took part
                {' · '}
                {results.totalVotes} / {results.totalEligibleVoters} voted
              </p>
            </section>
          </div>

          {/* Footer close */}
          <div className="shrink-0 px-5 sm:px-8 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <button
              onClick={onClose}
              className="
                w-full py-3 rounded-xl font-semibold text-white
                bg-gradient-to-r from-amber-500 to-yellow-500
                hover:from-amber-600 hover:to-yellow-600
                active:scale-[0.99] transition-all shadow-md
              "
            >
              Nice one — close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
